import { Fragment, useRef, useState } from 'react'
import {
  ChevronDown, ChevronUp, CircleStop, Maximize2, Pencil, Play, Plus, Trash2, User, X, ZoomIn, ZoomOut,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { APPROVAL_ITEMS, PALETTE, paletteOf } from './meta'
import { summary } from './model'
import type { PaletteItem, StepDraft } from './model'
import type { WorkflowPeopleView } from '../types'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 1.5

interface FlowCanvasProps {
  steps: StepDraft[]
  selectedKey: string | null
  /** Steps the server found a problem with, by key. */
  errorKeys: Set<string>
  people: WorkflowPeopleView | undefined
  fieldLabel: (name: string) => string
  trigger: string
  onSelect: (key: string) => void
  onInsert: (index: number, item: PaletteItem) => void
  onMove: (index: number, by: -1 | 1) => void
  onRemove: (index: number) => void
}

/**
 * The centre column: the flow as it will run, top to bottom.
 *
 * <p>A "+" sits on every connector, because "I need a notification between these two" is the
 * commonest edit and appending then dragging would make it three. It opens the same five kinds
 * the palette offers, inline, so nothing floats over the card it is about.
 *
 * <p>Zoom is a scale on the drawing, not on the page, so the toolbar and the panels stay
 * readable while a long flow is shrunk to fit.
 */
export function FlowCanvas({
  steps, selectedKey, errorKeys, people, fieldLabel, trigger,
  onSelect, onInsert, onMove, onRemove,
}: FlowCanvasProps) {
  const [zoom, setZoom] = useState(1)
  const [inserting, setInserting] = useState<number | null>(null)
  const viewport = useRef<HTMLDivElement>(null)
  const drawing = useRef<HTMLDivElement>(null)

  function fit() {
    const space = viewport.current?.clientHeight ?? 0
    const needed = drawing.current?.scrollHeight ?? 0
    // A test renderer measures nothing; leave the zoom alone rather than divide by zero.
    if (!space || !needed) return setZoom(1)
    setZoom(Math.max(MIN_ZOOM, Math.min(1, Math.floor((space / needed) * 10) / 10)))
  }

  function connector(index: number) {
    const open = inserting === index
    return (
      <div className="flex flex-col items-center">
        <span className="h-4 w-px bg-[var(--color-border-strong)]" aria-hidden="true" />
        <button
          type="button"
          aria-label={`Insert a step at position ${index + 1}`}
          aria-expanded={open}
          onClick={() => setInserting(open ? null : index)}
          className="flex size-6 items-center justify-center rounded-full border border-[var(--color-border-strong)] bg-[var(--color-card)] text-[var(--color-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
        >
          <Plus className="size-3.5" aria-hidden="true" />
        </button>
        {open && (
          <div
            role="menu"
            aria-label={`Step to insert at position ${index + 1}`}
            className="my-2 flex w-64 flex-col gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-2 shadow-md"
          >
            {APPROVAL_ITEMS.map((item) => {
              const meta = PALETTE[item]
              const Icon = meta.icon
              return (
                <button
                  key={item}
                  type="button"
                  role="menuitem"
                  onClick={() => { onInsert(index, item); setInserting(null) }}
                  className="flex items-center gap-2 rounded px-2 py-1 text-start text-sm hover:bg-[var(--color-page)]"
                >
                  <Icon className={`size-4 ${meta.tone}`} aria-hidden="true" />
                  {meta.label}
                </button>
              )
            })}
            <button
              type="button"
              onClick={() => setInserting(null)}
              className="mt-1 flex items-center justify-center gap-1 text-xs text-[var(--color-muted)]"
            >
              <X className="size-3" aria-hidden="true" /> Cancel
            </button>
          </div>
        )}
        <span className="h-4 w-px bg-[var(--color-border-strong)]" aria-hidden="true" />
      </div>
    )
  }

  return (
    <div className="flex min-h-[420px] flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-page)]">
      <div className="flex items-center justify-end gap-1 border-b border-[var(--color-border)] p-2">
        <Button type="button" variant="ghost" size="sm" aria-label="Fit to screen" onClick={fit}>
          <Maximize2 className="size-4" aria-hidden="true" />
        </Button>
        <Button
          type="button" variant="ghost" size="sm" aria-label="Zoom out"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => setZoom((value) => Math.max(MIN_ZOOM, Math.round((value - 0.1) * 10) / 10))}
        >
          <ZoomOut className="size-4" aria-hidden="true" />
        </Button>
        <button
          type="button"
          className="min-w-12 text-center text-xs tabular-nums text-[var(--color-body)]"
          aria-label="Reset zoom"
          onClick={() => setZoom(1)}
        >
          {Math.round(zoom * 100)}%
        </button>
        <Button
          type="button" variant="ghost" size="sm" aria-label="Zoom in"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => setZoom((value) => Math.min(MAX_ZOOM, Math.round((value + 0.1) * 10) / 10))}
        >
          <ZoomIn className="size-4" aria-hidden="true" />
        </Button>
      </div>

      <div ref={viewport} className="max-h-[70vh] flex-1 overflow-auto p-4">
        <div
          ref={drawing}
          className="mx-auto flex w-full max-w-md flex-col items-stretch"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
        >
          <div className="flex items-center gap-3 rounded-lg border-2 border-[var(--color-success)] bg-[var(--color-card)] p-3">
            <span className="flex size-9 items-center justify-center rounded-full bg-[var(--color-success)] text-white">
              <Play className="size-4" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-[var(--color-heading)]">Start</span>
              <span className="block text-xs text-[var(--color-muted)]">Trigger: {trigger}</span>
            </span>
          </div>

          <ol aria-label="Steps" className="flex flex-col">
            {steps.map((step, index) => {
              const meta = PALETTE[paletteOf(step)]
              const Icon = meta.icon
              const selected = step.key === selectedKey
              const broken = errorKeys.has(step.key)
              return (
                <Fragment key={step.key}>
                  <li>{connector(index)}</li>
                  <li
                    aria-label={`Step ${index + 1}: ${step.name || meta.label}`}
                    aria-current={selected ? 'step' : undefined}
                    className={`rounded-lg border-2 bg-[var(--color-card)] p-3 shadow-sm ${
                      selected ? 'border-[var(--color-warning)]'
                        : broken ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]'}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs font-semibold text-[var(--color-primary-fg)]">
                        {index + 1}
                      </span>
                      <span className={`flex size-8 shrink-0 items-center justify-center rounded-md ${meta.tint}`}>
                        <Icon className={`size-4 ${meta.tone}`} aria-hidden="true" />
                      </span>
                      <button
                        type="button"
                        onClick={() => onSelect(step.key)}
                        className="min-w-0 flex-1 text-start"
                      >
                        <span className="block truncate text-sm font-semibold text-[var(--color-heading)]">
                          {step.name || meta.label}
                        </span>
                        {step.description && (
                          <span className="block truncate text-xs text-[var(--color-muted)]">{step.description}</span>
                        )}
                        <span className="mt-1 flex items-center gap-1 text-xs text-[var(--color-body)]">
                          <User className="size-3 shrink-0" aria-hidden="true" />
                          <span className="truncate">{summary(step, people, fieldLabel)}</span>
                        </span>
                        {broken && (
                          <span className="mt-1 block text-xs text-[var(--color-danger)]">Needs attention</span>
                        )}
                      </button>
                    </div>
                    <div className="mt-2 flex justify-end gap-1">
                      <Button
                        type="button" variant="ghost" size="sm" disabled={index === 0}
                        aria-label={`Move step ${index + 1} up`} onClick={() => onMove(index, -1)}
                      >
                        <ChevronUp className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button" variant="ghost" size="sm" disabled={index === steps.length - 1}
                        aria-label={`Move step ${index + 1} down`} onClick={() => onMove(index, 1)}
                      >
                        <ChevronDown className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button" variant="ghost" size="sm"
                        aria-label={`Edit step ${index + 1}`} onClick={() => onSelect(step.key)}
                      >
                        <Pencil className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button" variant="ghost" size="sm"
                        aria-label={`Delete step ${index + 1}`} onClick={() => onRemove(index)}
                      >
                        <Trash2 className="size-4 text-[var(--color-danger)]" aria-hidden="true" />
                      </Button>
                    </div>
                  </li>
                </Fragment>
              )
            })}
            <li>{connector(steps.length)}</li>
          </ol>

          <div className="flex items-center gap-3 rounded-lg border-2 border-[var(--color-border-strong)] bg-[var(--color-card)] p-3">
            <span className="flex size-9 items-center justify-center rounded-full bg-[var(--color-muted)] text-white">
              <CircleStop className="size-4" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-[var(--color-heading)]">End</span>
              <span className="block text-xs text-[var(--color-muted)]">The request is approved</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
